import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Layout from '../components/Layout';
import API_BASE_URL from '../config/api';
import './LegalPageForm.css';

const LegalPageForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [loading, setLoading] = useState(isEdit);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    status: 'active',
    metaDescription: '',
    metaKeywords: ''
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEdit) {
      fetchLegalPage();
    }
  }, [id]);

  // Auto-generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const fetchLegalPage = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await axios.get(`${API_BASE_URL}/admin/legal-pages/${id}`);
      
      if (response.data && response.data.success) {
        const page = response.data.data;
        if (page) {
          setFormData({
            title: page.title || '',
            slug: page.slug || '',
            content: page.content || '',
            status: page.status || 'active',
            metaDescription: page.metaDescription || '',
            metaKeywords: page.metaKeywords || ''
          });
        } else {
          setError('Legal page data not found in response');
        }
      } else {
        setError('Invalid response from server');
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching legal page:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to fetch legal page';
      setError(`Error: ${errorMessage}${error.response?.status ? ` (Status: ${error.response.status})` : ''}`);
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    // Auto-generate slug from title (only when creating new)
    if (name === 'title' && !isEdit) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value)
      }));
    }
  };

  const handleContentChange = (value) => {
    setFormData({
      ...formData,
      content: value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Validate content (remove HTML tags and check if there's actual content)
      const textContent = formData.content.replace(/<[^>]*>/g, '').trim();
      if (!textContent) {
        setError('Content is required. Please add some content to the page.');
        setSaving(false);
        return;
      }
      
      if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/legal-pages/${id}`, formData);
      } else {
        await axios.post(`${API_BASE_URL}/admin/legal-pages`, formData);
      }
      navigate('/sy-admin/legal-pages');
    } catch (error) {
      setError(error.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} legal page`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="legal-page-form-container">
          <div className="legal-page-form-header">
            <div className="skeleton skeleton-text" style={{ width: '300px', height: '40px', borderRadius: '8px' }}></div>
            <div className="skeleton skeleton-button" style={{ width: '180px', height: '40px', borderRadius: '8px' }}></div>
          </div>
          <div className="legal-page-form-card">
            {/* Page Details Section */}
            <div className="form-section">
              <div className="skeleton skeleton-text" style={{ width: '200px', height: '32px', borderRadius: '8px', marginBottom: '24px' }}></div>
              
              {/* Title Field */}
              <div className="form-group">
                <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
              </div>

              {/* Slug Field */}
              <div className="form-group">
                <div className="skeleton skeleton-text" style={{ width: '80px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '250px', height: '12px', borderRadius: '4px', marginTop: '4px' }}></div>
              </div>

              {/* Content Editor */}
              <div className="form-group">
                <div className="skeleton skeleton-text" style={{ width: '100px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '100%', height: '50px', borderRadius: '8px', marginBottom: '4px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '100%', height: '300px', borderRadius: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '200px', height: '12px', borderRadius: '4px', marginTop: '4px' }}></div>
              </div>

              {/* Status Field */}
              <div className="form-group">
                <div className="skeleton skeleton-text" style={{ width: '80px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '200px', height: '44px', borderRadius: '8px' }}></div>
              </div>
            </div>

            {/* SEO Settings Section */}
            <div className="form-section">
              <div className="skeleton skeleton-text" style={{ width: '200px', height: '32px', borderRadius: '8px', marginBottom: '24px' }}></div>
              
              {/* Meta Description Field */}
              <div className="form-group">
                <div className="skeleton skeleton-text" style={{ width: '150px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '100%', height: '80px', borderRadius: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '100px', height: '12px', borderRadius: '4px', marginTop: '4px' }}></div>
              </div>

              {/* Meta Keywords Field */}
              <div className="form-group">
                <div className="skeleton skeleton-text" style={{ width: '130px', height: '16px', borderRadius: '4px', marginBottom: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '100%', height: '44px', borderRadius: '8px' }}></div>
                <div className="skeleton skeleton-text" style={{ width: '200px', height: '12px', borderRadius: '4px', marginTop: '4px' }}></div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <div className="skeleton skeleton-button" style={{ width: '150px', height: '44px', borderRadius: '8px' }}></div>
              <div className="skeleton skeleton-button" style={{ width: '100px', height: '44px', borderRadius: '8px' }}></div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="legal-page-form-container">
        <div className="legal-page-form-header">
          <h1>{isEdit ? 'Edit Legal Page' : 'Create New Legal Page'}</h1>
          <button
            onClick={() => navigate('/sy-admin/legal-pages')}
            className="btn btn-secondary"
          >
            ← Back to Legal Pages
          </button>
        </div>

        {error && (
          <div className="error-message">
            <span className="error-icon">⚠️</span>
            {error}
          </div>
        )}

        <div className="legal-page-form-card">
          <form onSubmit={handleSubmit}>
            <div className="form-section">
              <h2>Page Details</h2>
              
              <div className="form-group">
                <label htmlFor="title">Title *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  required
                  placeholder="e.g., Terms and Conditions"
                />
              </div>

              <div className="form-group">
                <label htmlFor="slug">Slug *</label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  required
                  placeholder="e.g., terms-and-conditions"
                />
                <small>URL-friendly identifier {!isEdit && '(auto-generated from title)'}</small>
              </div>

              <div className="form-group">
                <label htmlFor="content">Content *</label>
                <ReactQuill
                  theme="snow"
                  value={formData.content}
                  onChange={handleContentChange}
                  modules={{
                    toolbar: [
                      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                      ['bold', 'italic', 'underline', 'strike'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                      [{ 'script': 'sub'}, { 'script': 'super' }],
                      [{ 'indent': '-1'}, { 'indent': '+1' }],
                      [{ 'direction': 'rtl' }],
                      [{ 'size': ['small', false, 'large', 'huge'] }],
                      [{ 'color': [] }, { 'background': [] }],
                      [{ 'font': [] }],
                      [{ 'align': [] }],
                      ['clean'],
                      ['link', 'image', 'video'],
                      ['blockquote', 'code-block']
                    ]
                  }}
                  formats={[
                    'header', 'bold', 'italic', 'underline', 'strike',
                    'list', 'bullet', 'script', 'indent', 'direction',
                    'size', 'color', 'background', 'font', 'align',
                    'link', 'image', 'video', 'blockquote', 'code-block'
                  ]}
                  placeholder="Enter content here..."
                  className="html-editor-wrapper"
                />
                <small>Use the toolbar above to format your content</small>
              </div>

              <div className="form-group">
                <label htmlFor="status">Status *</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  required
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="form-section">
              <h2>SEO Settings</h2>
              
              <div className="form-group">
                <label htmlFor="metaDescription">Meta Description</label>
                <textarea
                  id="metaDescription"
                  name="metaDescription"
                  value={formData.metaDescription}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Brief description for search engines (recommended: 150-160 characters)"
                  maxLength="160"
                />
                <small>{formData.metaDescription.length}/160 characters</small>
              </div>

              <div className="form-group">
                <label htmlFor="metaKeywords">Meta Keywords</label>
                <input
                  type="text"
                  id="metaKeywords"
                  name="metaKeywords"
                  value={formData.metaKeywords}
                  onChange={handleChange}
                  placeholder="Comma-separated keywords (e.g., terms, conditions, policy)"
                />
                <small>Separate keywords with commas</small>
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Saving...' : (isEdit ? 'Update Page' : 'Create Page')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/sy-admin/legal-pages')}
                className="btn btn-secondary"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default LegalPageForm;
